// Presentation of the staff side of a call (roadmap #41). Same VideoContext and the same
// always-mounted media elements as the caller view; only the labels and the hang-up path
// differ (staff uses hangupCall, which tears down and returns to the dashboard).
import { useContext, useEffect, useRef, useState } from 'react'
import VideoContext from '../core/VideoContext'
import { convertTimerFormat } from '../core/callStatus'
import { globals } from '../core/globals'
import CallControls from '../components/CallControls'
import { MicOffIcon } from '../components/icons'
import { t } from '../i18n'

const CONTROLS_AUTOHIDE_MS = 4000

const callerLabel = () => {
  const c = globals.callerDetail || {}
  const raw = c.name || c.email || c.phone || ''
  if (!raw) return t.staffDefaultCaller
  return raw.includes('@') ? raw.split('@')[0] : raw
}

const initialsOf = (name: string) => {
  if (!name) return '··'
  const parts = name.split(/[\s._-]+/).filter(Boolean)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name.slice(0, 2).toUpperCase()
}

const StaffCallView = () => {
  const {
    sec,
    myVideo,
    userVideo,
    userAudio,
    isMyVideoOn,
    userVdoStatus,
    myMicStatus,
    userMicStatus,
    updateMic,
    webrtcState,
    callType,
    toggleVideoCall,
    switchCam,
    supportsMultipleCameras,
    isCallConnected,
    hangupCall,
  } = useContext(VideoContext)

  const [controlsVisible, setControlsVisible] = useState(true)
  const hideTimer = useRef<any>(null)

  const name = callerLabel()
  const degraded = webrtcState === 'disconnected' || webrtcState === 'failed'
  const showRemoteVideo = callType === 'video' && userVdoStatus

  const pokeControls = () => {
    setControlsVisible(true)
    clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setControlsVisible(false), CONTROLS_AUTOHIDE_MS)
  }

  useEffect(() => {
    if (isCallConnected) pokeControls()
    return () => clearTimeout(hideTimer.current)
  }, [isCallConnected])

  return (
    <div className="cf-call-stage cf-staff-stage" style={{ display: 'block' }} onClick={pokeControls}>
      <video
        id="remoteVideo"
        ref={userVideo}
        autoPlay
        playsInline
        className="cf-remote-video"
        style={{ visibility: showRemoteVideo ? 'visible' : 'hidden' }}
      />
      <audio id="remoteAudio" ref={userAudio} autoPlay />

      {!showRemoteVideo && (
        <div className="cf-remote-placeholder">
          <div className="cf-avatar">{initialsOf(name)}</div>
          <div className="cf-remote-name">{name}</div>
          {!isCallConnected && <div className="cf-remote-hint">{t.staffConnectingCall}</div>}
          {isCallConnected && callType === 'video' && <div className="cf-remote-hint">{t.cameraOff}</div>}
        </div>
      )}

      {isCallConnected && <div className="cf-timer">{convertTimerFormat(sec)}</div>}
      {isCallConnected && degraded && <div className="cf-degraded">{t.unstableConnection}</div>}
      {userMicStatus === false && (
        <div className="cf-remote-muted">
          <MicOffIcon />
          {t.staffCustomerMicOff}
        </div>
      )}

      <video
        ref={myVideo}
        autoPlay
        muted
        playsInline
        className="cf-local-preview"
        style={{ display: isMyVideoOn && callType === 'video' ? 'block' : 'none' }}
      />

      <CallControls
        micOn={Boolean(myMicStatus)}
        videoOn={callType === 'video'}
        canSwitchCam={Boolean(supportsMultipleCameras && callType === 'video')}
        visible={controlsVisible}
        onToggleMic={updateMic}
        onToggleVideo={toggleVideoCall}
        onSwitchCam={switchCam}
        onHangup={() => hangupCall()}
        poke={pokeControls}
      />
    </div>
  )
}

export default StaffCallView
